import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSEO } from "@/hooks/useSEO";
import { BRAND_HANDLE, BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import { BaseTemplate } from "@/components/templates/BaseTemplate";

/**
 * Bogotá — Public SEO landing page using BaseTemplate (rich layout + admin control).
 * Supports /bogota and /en/bogota. Stripe for Colombia (intl). Same admin control surface as Argentina BaseTemplate pages.
 */
const Bogota = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const isEn = location.pathname.startsWith("/en/") || location.pathname.startsWith("/en");

  useEffect(() => {
    if (isEn && i18n.language?.split("-")[0] !== "en") {
      void i18n.changeLanguage("en");
    }
  }, [isEn, i18n]);

  const locale = isEn ? "en_US" : "es_CO";
  const title = isEn
    ? `${BRAND_PERSON_NAME} | Bogotá — Content Creator | Exclusive Colombia Access`
    : `${BRAND_PERSON_NAME} | Bogotá — Creadora de Contenido | Acceso Exclusivo Colombia`;
  const description = isEn
    ? `Premium lifestyle, fitness and beauty content for Bogotá & Colombia. Join ${BRAND_PERSON_NAME}'s private community (Stripe checkout).`
    : `Contenido premium de lifestyle, fitness y belleza para Bogotá y Colombia. Únete a la comunidad privada de ${BRAND_PERSON_NAME} (pago Stripe).`;
  const keywords = isEn
    ? `${BRAND_PERSON_NAME}, bogota, bogotá, content creator bogota, colombia exclusive, fitness bogota, premium membership stripe`
    : `${BRAND_PERSON_NAME}, bogota, bogotá, creadora bogota, colombia exclusivo, fitness bogota, membresia premium stripe`;

  useSEO({
    title,
    description,
    keywords,
    ogImage: `${BRAND_SITE_URL}/og.png`,
    ogTitle: title,
    ogDescription: description,
    canonical: `${BRAND_SITE_URL}${location.pathname.replace(/\/$/, "") || "/bogota"}`,
    author: BRAND_PERSON_NAME,
    locale,
    alternateLocale: isEn ? "es_CO" : "en_US",
    type: "website",
    publishedTime: "2025-01-01T00:00:00.000Z",
    modifiedTime: new Date().toISOString(),
  });

  const heroTitle = isEn ? `${BRAND_PERSON_NAME} in Bogotá` : `${BRAND_PERSON_NAME} Bogotá`;
  const heroDescription = isEn
    ? "Exclusive content for my Bogotá community. Private membership with Stripe."
    : "Contenido exclusivo para mi comunidad bogotana. Membresía privada con Stripe.";

  return (
    <BaseTemplate
      heroTitle={heroTitle}
      heroDescription={heroDescription}
      showSobreMi={false}
      showPortfolioNavLink
      showGalleryNavLink
      showContactNavLink
      showGallerySection
      showTallySection
    />
  );
};

export default Bogota;
